import { Form, Formik, Field } from "formik";
import React, { useEffect, useState } from "react";
import Fields from "@components/fields";
import { HexColorPicker } from "react-colorful";
import { useModal } from "@context/ModalContext";
import Button from "@components/button";
import { FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import {
  BottomActions,
  ButtonsContainer,
  ColorBox,
  ColorCode,
  ColorItem,
  ColorList,
  ColorName,
  ColorPreviewBox,
  ColorPreviewWrapper,
  Container,
  ContentWrapper,
  DeleteIcon,
  EmptyMessage,
  HexInput,
  ModalContent,
  ModalTitle,
  PickerWrapper,
  RightPanel,
  SavedColorsWrapper,
  SectionTitle,
  StyledForm,
  StyledHexPicker,
} from "./style";
import { Color } from "@apis/index";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

const ColorForm = () => {
  const { items: savedColors } = useSelector(
    (state: RootState) => state.colors,
  );

  const fetchColors = async () => {
    await Color.list({ state: "colors" });
  };

  useEffect(() => {
    fetchColors();
  }, []);

  const { state } = useLocation();
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const [colors, setColors] = useState<{ name: string; hex: string }[]>([]);

  const handleCloseModal = () => {
    hideModal();
    navigate("/admin/product-management/colors");
  };

  // 🔁 If editing existing color
  useEffect(() => {
    if (state?.id) {
      showModal(
        <UpdateColorModal
          id={state.id}
          color={{ name: state.name, hex: state.code }}
          savedColors={savedColors}
          onClose={handleCloseModal}
        />,
      );
    }
  }, [state?.id, savedColors]);

  // ➕ Add new color
  const handleAddColor = () => {
    showModal(
      <ModalContent>
        <ModalTitle>Pick a Color</ModalTitle>

        <Formik
          initialValues={{ name: "", hex: "#3498db" }}
          onSubmit={(values, { resetForm }) => {
            const nameExists =
              savedColors.some(
                (c) => c.name.toLowerCase() === values.name.toLowerCase(),
              ) ||
              colors.some(
                (c) => c.name.toLowerCase() === values.name.toLowerCase(),
              );

            const hexExists =
              savedColors.some(
                (c) => c.code.toLowerCase() === values.hex.toLowerCase(),
              ) ||
              colors.some(
                (c) => c.hex.toLowerCase() === values.hex.toLowerCase(),
              );

            if (!values.name) {
              toast.error("Color name is required");
              return;
            }
            if (nameExists) {
              toast.error("This color name already exists");
              return;
            }
            if (hexExists) {
              toast.error("This color code already exists");
              return;
            }

            setColors((prev) => [...prev, values]);
            resetForm();
            hideModal();
          }}
        >
          {({ values, setFieldValue, handleSubmit }) => (
            <StyledForm
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <PickerWrapper>
                <StyledHexPicker
                  color={values.hex}
                  onChange={(newColor) => setFieldValue("hex", newColor)}
                />
                <ColorPreviewWrapper>
                  <HexInput name="hex" type="text" readOnly />
                  <ColorPreviewBox bg={values.hex} />
                </ColorPreviewWrapper>
              </PickerWrapper>

              <Fields.Input
                name="name"
                label="Color Name"
                placeholder="Enter color name"
                styleInput="width:100%"
              />

              <ButtonsContainer>
                <Button title="Reset" type="reset" variant="reset" />
                <Button title="Save" type="submit" />
              </ButtonsContainer>
            </StyledForm>
          )}
        </Formik>
      </ModalContent>,
    );
  };

  // 🧹 Reset and Submit
  const handleResetColors = () => setColors([]);

  const handleSubmitColors = async () => {
    if (colors.length === 0) return toast.error("No colors to submit");

    const payload = {
      items: colors.map((c) => ({ name: c.name, code: c.hex })),
    };
    await Color.create({ state: "colors", data: payload }).then(() => {
      fetchColors();
      setColors([]);
    });
  };

  const handleRemoveColor = (index: number) => {
    setColors((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Container>
      <ContentWrapper>
        <SavedColorsWrapper>
          <SectionTitle>Saved Colors</SectionTitle>

          {colors.length === 0 ? (
            <EmptyMessage>No colors added yet.</EmptyMessage>
          ) : (
            <ColorList>
              {colors.map((c, i) => (
                <ColorItem key={i}>
                  <ColorBox color={c.hex} />
                  <ColorName>{c.name}</ColorName>
                  <ColorCode>{c.hex}</ColorCode>
                  <DeleteIcon onClick={() => handleRemoveColor(i)} />
                </ColorItem>
              ))}
            </ColorList>
          )}
        </SavedColorsWrapper>

        <RightPanel>
          <Button
            title="Pick Color"
            onClick={handleAddColor}
            styleButton="padding:10px;font-size:12px"
          />
        </RightPanel>
      </ContentWrapper>

      <BottomActions>
        <Button title="Reset" onClick={handleResetColors} variant="reset" />
        <Button title="Submit" onClick={handleSubmitColors} />
      </BottomActions>
    </Container>
  );
};

export default ColorForm;

// ------------------ Update Modal ------------------

interface UpdateColorModalProps {
  id: number;
  color: { name: string; hex: string };
  savedColors: any[];
  onClose: () => void;
}

const UpdateColorModal: React.FC<UpdateColorModalProps> = ({
  id,
  color,
  savedColors,
  onClose,
}) => {
  return (
    <ModalContent>
      <ModalTitle>Update Color</ModalTitle>

      <Formik
        initialValues={{ name: color.name, hex: color.hex }}
        onSubmit={async (values, { resetForm }) => {
          const nameExists = savedColors.some(
            (c) =>
              c.name.toLowerCase() === values.name.toLowerCase() && c.id !== id,
          );
          const hexExists = savedColors.some(
            (c) =>
              c.code.toLowerCase() === values.hex.toLowerCase() && c.id !== id,
          );

          if (!values.name) return toast.error("Color name is required");
          if (nameExists) return toast.error("This color name already exists");
          if (hexExists) return toast.error("This color code already exists");

          await Color.update({
            state: "colors",
            data: { name: values.name, code: values.hex },
            id,
          });

          resetForm();
          onClose();
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <StyledForm
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <PickerWrapper>
              <StyledHexPicker
                color={values.hex}
                onChange={(newColor) => setFieldValue("hex", newColor)}
              />
              <ColorPreviewWrapper>
                <HexInput name="hex" type="text" readOnly />
                <ColorPreviewBox bg={values.hex} />
              </ColorPreviewWrapper>
            </PickerWrapper>

            <Fields.Input
              name="name"
              label="Color Name"
              placeholder="Enter color name"
              styleInput="width:100%"
            />

            <ButtonsContainer>
              <Button title="Reset" type="reset" variant="reset" />
              <Button title="Update" type="submit" />
            </ButtonsContainer>
          </StyledForm>
        )}
      </Formik>
    </ModalContent>
  );
};
